import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../../lib/supabaseClient';
import logo from '../../assets/logo.png';
import Seo from '../../components/common/Seo';

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isAdminHost = hostname === 'admin.gamemeetsworld.com';

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (!email || !password) {
        setError('Please fill in all fields');
        return;
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password
      });

      if (error || !data.session) {
        setError('Invalid admin credentials');
        return;
      }

      const check = await fetch('/api/admin/check', {
        headers: { Authorization: `Bearer ${data.session.access_token}` }
      });

      if (!check.ok) {
        await supabase.auth.signOut();
        setError('Not authorized as admin');
        return;
      }

      navigate('/dashboard/admin');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-game-black text-white flex flex-col">
      <Seo
        title="Admin Login"
        description="Secure admin access for GameMeetsWorld."
        path="/login"
        noIndex
      />
      <header className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logo} alt="GameMeetsWorld" className="h-10 w-auto" />
            <span className="font-bold text-lg">GameMeetsWorld Admin</span>
          </div>
          {!isAdminHost && (
            <Link to="/" className="text-sm text-white/70 hover:text-[#A1FF4F]">
              Back to website
            </Link>
          )}
        </div>
      </header>

      <main className="flex-grow flex items-center justify-center px-6 py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold mb-2">
              <span className="text-accent-green">Admin Login</span>
            </h1>
            <p className="text-text-secondary">Admin access for managing the platform</p>
          </div>

          {/* Login Card */}
          <div className="bg-game-grey rounded-2xl border border-accent-green/20 p-8">
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200 text-sm"
              >
                {error}
              </motion.div>
            )}

            <form onSubmit={handleLogin} className="space-y-6" autoComplete="off">
              {/* Email Field */}
              <div>
                <label className="block text-sm font-semibold mb-2 text-text-secondary">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 bg-game-black border border-accent-green/30 rounded-lg text-white placeholder-text-tertiary focus:outline-none focus:border-accent-green transition-colors"
                  disabled={loading}
                  autoComplete="username"
                />
              </div>

              {/* Password Field */}
              <div>
                <label className="block text-sm font-semibold mb-2 text-text-secondary">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full px-4 py-3 bg-game-black border border-accent-green/30 rounded-lg text-white placeholder-text-tertiary focus:outline-none focus:border-accent-green transition-colors"
                  disabled={loading}
                  autoComplete="new-password"
                />
              </div>

              {/* Login Button */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={loading}
                className="w-full bg-accent-green text-black font-bold py-3 px-4 rounded-lg hover:shadow-lg shadow-accent-green/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Logging in...' : 'Login to Admin'}
              </motion.button>

            </form>
          </div>

          {!isAdminHost && (
            <div className="text-center mt-8">
              <Link
                to="/"
                className="text-text-secondary hover:text-accent-green transition-colors text-sm"
              >
                ← Back to Home
              </Link>
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
}
